// ═══════════════════════════════════════════════════════════════
// MÓDULO DE PRÉSTAMOS - Descuento automático por nómina
// Los descuentos se aplican durante el procesamiento de nómina
// mediante aplicarDescuentoPrestamo() (exportada al final)
// ═══════════════════════════════════════════════════════════════

import React, { useState } from 'react';
import { DollarSign, Plus, Edit2, Trash2, X, Banknote, Save, CreditCard } from 'lucide-react';
import useEmployees from '../../hooks/useEmployees';
import { usePrestamos } from '../../hooks/usePrestamos';
import Pagination from '../common/Pagination';

const PAGE_SIZE = 15;

const fmt = (n) =>
  parseFloat(n || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 });

function StatCard({ label, value, sub }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );
}

export default function PrestamosModule() {
  const { employees } = useEmployees();
  const { prestamos, loading, createPrestamo, updatePrestamo, deletePrestamo } = usePrestamos();

  const [showForm, setShowForm]           = useState(false);
  const [editingPrestamo, setEditingPrestamo] = useState(null);
  const [pagoExtraordinario, setPagoExtraordinario] = useState(null);
  const [page, setPage]                   = useState(1);

  const [formData, setFormData] = useState({
    empleadoId:        '',
    fechaSolicitud:    new Date().toISOString().split('T')[0],
    valorSolicitado:   0,
    tasaInteres:       10,
    cuotas:            0,
    quincenaDescuento: 'Q1',
    comentarios:       ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    const employee = employees.find(emp => emp.id === formData.empleadoId);
    if (!employee) { alert('Selecciona un empleado'); return; }

    const valorSolicitado = parseFloat(formData.valorSolicitado);
    const tasaInteres     = parseFloat(formData.tasaInteres);
    const cuotas          = parseInt(formData.cuotas);

    if (!valorSolicitado || valorSolicitado <= 0) { alert('El valor solicitado debe ser mayor a 0'); return; }
    if (!cuotas || cuotas <= 0)                   { alert('El número de cuotas debe ser mayor a 0'); return; }

    const montoInteres = valorSolicitado * (tasaInteres / 100);
    const valorTotal   = valorSolicitado + montoInteres;
    const cuotaMensual = valorTotal / cuotas;

    const prestamoData = {
      empleadoId:        formData.empleadoId,
      empleadoNombre:    `${employee.nombre} ${employee.apellido}`,
      empleadoCodigo:    employee.employeeId,
      fechaSolicitud:    formData.fechaSolicitud,
      valorSolicitado,
      tasaInteres,
      montoInteres,
      valorTotal,
      cuotaMensual,
      cuotas,
      tiempoMeses:       cuotas,
      cuotasPagadas:     0,
      valorPagado:       0,
      balance:           valorTotal,
      estado:            'activo',
      quincenaDescuento: formData.quincenaDescuento,
      comentarios:       formData.comentarios
    };

    try {
      if (editingPrestamo) {
        await updatePrestamo(editingPrestamo.id, prestamoData);
      } else {
        await createPrestamo(prestamoData);
      }
      handleCancel();
    } catch (error) {
      alert('Error al guardar préstamo: ' + error.message);
    }
  };

  const handleEdit = (prestamo) => {
    setEditingPrestamo(prestamo);
    setFormData({
      empleadoId:        prestamo.empleadoId,
      fechaSolicitud:    prestamo.fechaSolicitud,
      valorSolicitado:   prestamo.valorSolicitado,
      tasaInteres:       prestamo.tasaInteres,
      cuotas:            prestamo.cuotas,
      quincenaDescuento: prestamo.quincenaDescuento || 'Q1',
      comentarios:       prestamo.comentarios || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Eliminar este préstamo? Esta acción no se puede deshacer.')) {
      try {
        await deletePrestamo(id);
      } catch (error) {
        alert('Error al eliminar préstamo: ' + error.message);
      }
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingPrestamo(null);
    setFormData({
      empleadoId: '', fechaSolicitud: new Date().toISOString().split('T')[0],
      valorSolicitado: 0, tasaInteres: 10, cuotas: 0,
      quincenaDescuento: 'Q1', comentarios: ''
    });
  };

  const handlePagoExtraordinario = async () => {
    if (!pagoExtraordinario) return;
    const { prestamo, monto, fecha } = pagoExtraordinario;

    const montoNum = parseFloat(monto);
    if (!montoNum || montoNum <= 0)    { alert('El monto debe ser mayor a 0'); return; }
    if (montoNum > prestamo.balance)   { alert(`El monto no puede superar el balance pendiente (RD$ ${prestamo.balance.toLocaleString('es-DO', { minimumFractionDigits: 2 })})`); return; }
    if (!fecha)                        { alert('Selecciona la fecha del pago'); return; }

    const nuevoValorPagado    = prestamo.valorPagado + montoNum;
    const nuevoBalance        = Math.max(prestamo.valorTotal - nuevoValorPagado, 0);
    const nuevasCuotasPagadas = nuevoBalance <= 0
      ? prestamo.cuotas
      : Math.floor(nuevoValorPagado / prestamo.cuotaMensual);
    const nuevoEstado = nuevoBalance <= 0 ? 'completado' : 'activo';

    const historial = Array.isArray(prestamo.historialPagos) ? [...prestamo.historialPagos] : [];
    historial.push({ tipo: 'extraordinario', monto: montoNum, fecha, balanceAlPagar: nuevoBalance, fechaRegistro: new Date().toISOString() });

    try {
      await updatePrestamo(prestamo.id, {
        cuotasPagadas: nuevasCuotasPagadas,
        valorPagado:   nuevoValorPagado,
        balance:       nuevoBalance,
        estado:        nuevoEstado,
        historialPagos: historial
      });
      setPagoExtraordinario(null);
    } catch (error) {
      alert('Error al registrar pago: ' + error.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-3" />
          <p className="text-sm text-gray-400">Cargando préstamos...</p>
        </div>
      </div>
    );
  }

  const prestamosActivos    = prestamos.filter(p => p.estado === 'activo');
  const prestamosCompletados = prestamos.filter(p => p.estado === 'completado');
  const totalPrestado       = prestamos.reduce((s, p) => s + p.valorTotal, 0);
  const totalPendiente      = prestamosActivos.reduce((s, p) => s + p.balance, 0);

  const totalPagesPrestamos = Math.ceil(prestamos.length / PAGE_SIZE);
  const pagedPrestamos      = prestamos.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Vista previa del formulario
  const previewInterest  = formData.valorSolicitado * (formData.tasaInteres / 100);
  const previewTotal     = formData.valorSolicitado + previewInterest;
  const previewCuota     = formData.cuotas > 0 ? previewTotal / formData.cuotas : 0;
  const showPreview      = formData.valorSolicitado > 0 && formData.cuotas > 0;

  return (
    <div className="space-y-4">

      {/* ── Toolbar ──────────────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-4">
        <span className="text-sm text-gray-400 bg-gray-100 px-2.5 py-1 rounded-full font-medium">
          <span className="font-semibold text-gray-700">{prestamos.length}</span> préstamo{prestamos.length !== 1 ? 's' : ''}
        </span>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus size={15} />
          Nuevo Préstamo
        </button>
      </div>

      {/* ── Stat Cards ───────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total Préstamos" value={prestamos.length} />
        <StatCard label="Activos"         value={prestamosActivos.length} />
        <StatCard
          label="Total Prestado"
          value={<span className="text-lg">RD$ {(totalPrestado / 1000).toFixed(1)}K</span>}
        />
        <StatCard
          label="Pendiente Cobro"
          value={<span className="text-lg">RD$ {(totalPendiente / 1000).toFixed(1)}K</span>}
        />
      </div>

      {/* ── Formulario ───────────────────────────────────────────── */}
      {showForm && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <CreditCard size={16} className="text-blue-600" />
              <h3 className="text-sm font-bold text-gray-900">
                {editingPrestamo ? 'Editar Préstamo' : 'Nuevo Préstamo'}
              </h3>
            </div>
            <button
              onClick={handleCancel}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X size={15} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">

              {/* Empleado */}
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Empleado *</label>
                <select
                  value={formData.empleadoId}
                  onChange={(e) => setFormData({ ...formData, empleadoId: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 bg-white"
                  required
                >
                  <option value="">Seleccionar empleado</option>
                  {employees.filter(e => e.estado === 'activo').map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.nombre} {emp.apellido} — {emp.employeeId}
                    </option>
                  ))}
                </select>
              </div>

              {/* Fecha solicitud */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Fecha de Solicitud *</label>
                <input
                  type="date"
                  value={formData.fechaSolicitud}
                  onChange={(e) => setFormData({ ...formData, fechaSolicitud: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
                  required
                />
              </div>

              {/* Valor solicitado */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Valor Solicitado (RD$) *</label>
                <input
                  type="number" step="0.01"
                  value={formData.valorSolicitado}
                  onChange={(e) => setFormData({ ...formData, valorSolicitado: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
                  required
                />
              </div>

              {/* Tasa interés */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Tasa de Interés (%) *</label>
                <input
                  type="number" step="0.01"
                  value={formData.tasaInteres}
                  onChange={(e) => setFormData({ ...formData, tasaInteres: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
                  required
                />
              </div>

              {/* Cuotas */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Número de Cuotas *</label>
                <input
                  type="number" min="1"
                  value={formData.cuotas}
                  onChange={(e) => setFormData({ ...formData, cuotas: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
                  required
                />
              </div>

              {/* Quincena */}
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-2">Descontar en Quincena *</label>
                <div className="flex gap-2">
                  {[
                    { val: 'Q1',    label: '1ra quincena' },
                    { val: 'Q2',    label: '2da quincena' },
                    { val: 'AMBAS', label: 'Ambas (Q1+Q2)' },
                  ].map(({ val, label }) => (
                    <button
                      key={val}
                      type="button"
                      onClick={() => setFormData({ ...formData, quincenaDescuento: val })}
                      className={`flex-1 px-3 py-2 text-sm font-semibold rounded-lg border transition-colors ${
                        formData.quincenaDescuento === val
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-400 mt-1.5">
                  {formData.quincenaDescuento === 'AMBAS'
                    ? 'Se descontará la mitad de la cuota mensual en cada quincena'
                    : `Se descontará la cuota completa en la ${formData.quincenaDescuento === 'Q1' ? 'primera' : 'segunda'} quincena`}
                </p>
              </div>

              {/* Comentarios */}
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Comentarios</label>
                <textarea
                  value={formData.comentarios}
                  onChange={(e) => setFormData({ ...formData, comentarios: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
                  rows="2"
                  placeholder="Detalles adicionales del préstamo..."
                />
              </div>
            </div>

            {/* Vista previa */}
            {showPreview && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Vista previa del préstamo</p>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-xs text-gray-500">Interés ({formData.tasaInteres}%)</p>
                    <p className="font-bold text-gray-900">RD$ {fmt(previewInterest)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Valor Total a Pagar</p>
                    <p className="font-bold text-gray-900">RD$ {fmt(previewTotal)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Cuota Mensual</p>
                    <p className="font-bold text-blue-600">RD$ {fmt(previewCuota)}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center gap-3">
              <button
                type="submit"
                className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Save size={14} />
                {editingPrestamo ? 'Actualizar' : 'Crear'} Préstamo
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="flex items-center gap-2 px-5 py-2 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <X size={14} />
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── Tabla de Préstamos ────────────────────────────────────── */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-blue-600 text-white">
                <th className="px-4 py-3 text-left   text-xs font-semibold uppercase tracking-wider">No.</th>
                <th className="px-4 py-3 text-left   text-xs font-semibold uppercase tracking-wider">Empleado</th>
                <th className="px-4 py-3 text-left   text-xs font-semibold uppercase tracking-wider">Fecha</th>
                <th className="px-4 py-3 text-right  text-xs font-semibold uppercase tracking-wider">Valor</th>
                <th className="px-4 py-3 text-right  text-xs font-semibold uppercase tracking-wider">Total</th>
                <th className="px-4 py-3 text-right  text-xs font-semibold uppercase tracking-wider">Cuota</th>
                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">Quincena</th>
                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">Cuotas</th>
                <th className="px-4 py-3 text-right  text-xs font-semibold uppercase tracking-wider">Balance</th>
                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">Estado</th>
                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">Acciones</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {prestamos.length === 0 ? (
                <tr>
                  <td colSpan="11" className="px-6 py-16 text-center">
                    <DollarSign className="w-10 h-10 text-gray-200 mx-auto mb-3" />
                    <p className="text-sm font-medium text-gray-500">No hay préstamos registrados</p>
                    <p className="text-xs text-gray-400 mt-1">Crea el primer préstamo para comenzar</p>
                  </td>
                </tr>
              ) : (
                pagedPrestamos.map((prestamo, index) => (
                  <tr key={prestamo.id} className="hover:bg-gray-50 transition-colors">

                    <td className="px-4 py-3 text-sm font-semibold text-gray-500">{index + 1}</td>

                    <td className="px-4 py-3">
                      <p className="text-sm font-semibold text-gray-900">{prestamo.empleadoNombre}</p>
                      <p className="text-xs text-gray-400">{prestamo.empleadoCodigo}</p>
                    </td>

                    <td className="px-4 py-3 text-sm text-gray-700">
                      {new Date(String(prestamo.fechaSolicitud).split('T')[0] + 'T12:00:00').toLocaleDateString('es-DO')}
                    </td>

                    <td className="px-4 py-3 text-right text-sm font-medium text-gray-900">
                      RD$ {fmt(prestamo.valorSolicitado)}
                    </td>

                    <td className="px-4 py-3 text-right text-sm font-semibold text-gray-900">
                      RD$ {fmt(prestamo.valorTotal)}
                    </td>

                    <td className="px-4 py-3 text-right text-sm font-semibold text-blue-600">
                      RD$ {fmt(prestamo.cuotaMensual)}
                    </td>

                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                        prestamo.quincenaDescuento === 'Q1'    ? 'bg-blue-100 text-blue-700' :
                        prestamo.quincenaDescuento === 'Q2'    ? 'bg-green-100 text-green-700' :
                                                                 'bg-purple-100 text-purple-700'
                      }`}>
                        {prestamo.quincenaDescuento || 'Q1'}
                      </span>
                    </td>

                    <td className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                      {prestamo.cuotasPagadas} / {prestamo.cuotas}
                    </td>

                    <td className="px-4 py-3 text-right text-sm font-semibold text-orange-600">
                      RD$ {fmt(prestamo.balance)}
                    </td>

                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                        prestamo.estado === 'completado'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-orange-100 text-orange-700'
                      }`}>
                        {prestamo.estado === 'completado' ? 'Completado' : 'En Curso'}
                      </span>
                    </td>

                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        {prestamo.estado === 'activo' && (
                          <button
                            onClick={() => setPagoExtraordinario({
                              prestamo,
                              monto: '',
                              fecha: new Date().toISOString().split('T')[0]
                            })}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Pago extraordinario"
                          >
                            <Banknote className="w-3.5 h-3.5" />
                            Pago
                          </button>
                        )}
                        <button
                          onClick={() => handleEdit(prestamo)}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                          Editar
                        </button>
                        <button
                          onClick={() => handleDelete(prestamo.id)}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          Eliminar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {prestamos.length > 0 && (
              <tfoot className="bg-gray-50 border-t border-gray-200">
                <tr>
                  <td colSpan="3" className="px-4 py-3 text-xs text-gray-500">
                    <span className="font-semibold text-gray-700">{prestamosActivos.length}</span> activos{' · '}
                    <span className="font-semibold text-gray-700">{prestamosCompletados.length}</span> completados
                  </td>
                  <td className="px-4 py-3 text-right text-xs text-gray-500">
                    RD$ {fmt(totalPrestado)}
                  </td>
                  <td colSpan="4" />
                  <td className="px-4 py-3 text-right">
                    <p className="text-xs text-gray-400">Pendiente</p>
                    <p className="text-sm font-bold text-orange-600">RD$ {fmt(totalPendiente)}</p>
                  </td>
                  <td colSpan="2" />
                </tr>
              </tfoot>
            )}
          </table>
          <Pagination
            page={page}
            totalPages={totalPagesPrestamos}
            total={prestamos.length}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
          />
        </div>
      </div>

      {/* ── Modal: Pago Extraordinario ───────────────────────────── */}
      {pagoExtraordinario && (() => {
        const p            = pagoExtraordinario.prestamo;
        const montoNum     = parseFloat(pagoExtraordinario.monto) || 0;
        const nuevoBalance = p.balance - montoNum;
        const nuevasCuotasRest = nuevoBalance <= 0 ? 0 : Math.ceil(nuevoBalance / p.cuotaMensual);
        const validMonto   = montoNum > 0 && montoNum <= p.balance && pagoExtraordinario.fecha;

        return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">

              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Banknote className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-900">Pago Extraordinario</h3>
                    <p className="text-xs text-gray-500">{p.empleadoNombre} · {p.empleadoCodigo}</p>
                  </div>
                </div>
                <button
                  onClick={() => setPagoExtraordinario(null)}
                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X size={15} />
                </button>
              </div>

              {/* Body */}
              <div className="p-6 space-y-4">

                {/* Info actual */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Balance actual</p>
                      <p className="font-bold text-orange-600">RD$ {fmt(p.balance)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Cuota mensual</p>
                      <p className="font-bold text-gray-900">RD$ {fmt(p.cuotaMensual)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Cuotas restantes</p>
                      <p className="font-bold text-gray-900">{p.cuotas - Math.floor(p.cuotasPagadas)}</p>
                    </div>
                  </div>
                </div>

                {/* Monto */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Monto del Pago (RD$) *</label>
                  <input
                    type="number" step="0.01" min="0.01" max={p.balance}
                    value={pagoExtraordinario.monto}
                    onChange={(e) => setPagoExtraordinario({ ...pagoExtraordinario, monto: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
                    placeholder="0.00"
                    autoFocus
                  />
                  {montoNum > p.balance && (
                    <p className="text-xs text-red-600 mt-1">El monto no puede superar el balance pendiente</p>
                  )}
                </div>

                {/* Fecha */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Fecha del Pago *</label>
                  <input
                    type="date"
                    value={pagoExtraordinario.fecha}
                    onChange={(e) => setPagoExtraordinario({ ...pagoExtraordinario, fecha: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
                  />
                </div>

                {/* Preview */}
                {montoNum > 0 && montoNum <= p.balance && (
                  <div className={`rounded-lg p-4 border ${nuevoBalance <= 0 ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'}`}>
                    <p className={`text-xs font-semibold uppercase tracking-wider mb-3 ${nuevoBalance <= 0 ? 'text-green-700' : 'text-blue-700'}`}>
                      {nuevoBalance <= 0 ? '✓ Este pago cancelará el préstamo completo' : 'Recálculo tras el pago'}
                    </p>
                    <div className="grid grid-cols-3 gap-3 text-sm">
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Nuevo balance</p>
                        <p className={`font-bold ${nuevoBalance <= 0 ? 'text-green-700' : 'text-blue-700'}`}>
                          RD$ {fmt(Math.max(nuevoBalance, 0))}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Cuotas restantes</p>
                        <p className={`font-bold ${nuevoBalance <= 0 ? 'text-green-700' : 'text-blue-700'}`}>
                          {nuevasCuotasRest}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Estado</p>
                        <p className={`font-bold ${nuevoBalance <= 0 ? 'text-green-700' : 'text-blue-700'}`}>
                          {nuevoBalance <= 0 ? 'Completado' : 'En Curso'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Botones */}
                <div className="flex gap-3 pt-1">
                  <button
                    onClick={handlePagoExtraordinario}
                    disabled={!validMonto}
                    className="flex-1 flex items-center justify-center gap-2 px-5 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Banknote size={14} />
                    Registrar Pago
                  </button>
                  <button
                    onClick={() => setPagoExtraordinario(null)}
                    className="px-5 py-2.5 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// UTILIDAD EXPORTADA — la consume el procesamiento de nómina
// ═══════════════════════════════════════════════════════════════
// Recibe un préstamo activo y la quincena que se está procesando
// ('Q1' o 'Q2').
//
// Retorna { montoDescuento, updatePayload } si el descuento aplica
// esta quincena, o null si no aplica.
//
// El llamador (nómina) es quien ejecuta el updatePrestamo:
//   import { aplicarDescuentoPrestamo } from '../Prestamos/PrestamosModule';
//   const resultado = aplicarDescuentoPrestamo(prestamo, 'Q1');
//   if (resultado) {
//     totalDescuentos += resultado.montoDescuento;
//     await updatePrestamo(prestamo.id, resultado.updatePayload);
//   }
// ═══════════════════════════════════════════════════════════════
export function aplicarDescuentoPrestamo(prestamo, quincenaActual) {
  // Solo aplica a préstamos activos con balance pendiente
  if (!prestamo || prestamo.estado !== 'activo' || prestamo.balance <= 0) {
    return null;
  }

  // Ya completó todas las cuotas
  if (prestamo.cuotasPagadas >= prestamo.cuotas) {
    return null;
  }

  const quincenaDescuento = prestamo.quincenaDescuento || 'Q1';

  // Determinar si esta quincena debe aplicar el descuento
  let aplica = false;
  let montoDescuento = 0;

  if (quincenaDescuento === 'AMBAS') {
    // Se desconta la mitad de la cuota mensual en cada quincena
    aplica = true;
    montoDescuento = prestamo.cuotaMensual / 2;
  } else if (quincenaDescuento === quincenaActual) {
    // Se desconta la cuota completa solo en la quincena asignada
    aplica = true;
    montoDescuento = prestamo.cuotaMensual;
  }

  if (!aplica) {
    return null;
  }

  // Calcular nuevo estado después del descuento
  const nuevasCuotasPagadas = quincenaDescuento === 'AMBAS'
    ? prestamo.cuotasPagadas + 0.5   // media cuota por quincena
    : prestamo.cuotasPagadas + 1;    // cuota completa

  const nuevoValorPagado = prestamo.valorPagado + montoDescuento;
  const nuevoBalance = Math.max(prestamo.valorTotal - nuevoValorPagado, 0);
  const nuevoEstado = nuevoBalance <= 0 ? 'completado' : 'activo';

  return {
    montoDescuento,
    updatePayload: {
      cuotasPagadas: nuevasCuotasPagadas,
      valorPagado: nuevoValorPagado,
      balance: nuevoBalance,
      estado: nuevoEstado
    }
  };
}
